import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Sidebar } from './components/Sidebar/Sidebar';
import { Header } from './components/Header/Header.new';
import { FeatureProvider } from './contexts/FeatureContext';
import { FeatureGate } from './components/FeatureGate';
import './App.css';

// Pages
import { MenuEditor } from './pages/MenuEditor/MenuEditor';
import MenuManagement from './pages/MenuManagement.jsx';
import { Dashboard } from './pages/Dashboard';
import { DocumentUpload } from './pages/DocumentUpload';
import { DocumentManagement } from './pages/DocumentManagement';
import EventsManagement from './pages/EventsManagement';
import './pages/EventsManagement.css';
import Login from './pages/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import UserProvider from './contexts/UserContext';
import { TenantProvider } from './contexts/TenantContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import BrandingPage from './pages/Branding';
import MenuTranslation from './components/MenuTranslation';
import PaymentSettingsPage from './pages/PaymentSettings';
import OrdersManagementPage from './pages/Orders/OrdersManagement';
import DeliveryTouchPage from './pages/Orders/DeliveryTouchPage';
import PrintCenterPage from './pages/PrintCenter/PrintCenterPage';
import CashflowPage from './pages/Cashflow/CashflowPage';
import PosPage from './pages/Pos/PosPage';
import StaffManagement from './pages/StaffManagement/StaffManagement';
import StaffPermissions from './pages/StaffManagement/StaffPermissions';
import ClientFeatures from './pages/ClientFeatures/ClientFeatures';
import { SupportTickets } from './pages/Support/SupportTickets';
import { TicketDetail } from './pages/Support/TicketDetail';
import { PermissionGate } from './components/PermissionGate';

// Componente per le rotte protette
const ProtectedLayout = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated } = useAuth();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return (
        <div className={`app-container${sidebarCollapsed ? ' app-container--sidebar-collapsed' : ''}`}>
            <Sidebar
                collapsed={sidebarCollapsed}
                onToggle={() => setSidebarCollapsed(prev => !prev)}
                mobileOpen={mobileMenuOpen}
                onMobileToggle={() => setMobileMenuOpen(prev => !prev)}
            />
            <div className="main-content">
                <Header
                    sidebarCollapsed={sidebarCollapsed}
                    onToggleSidebar={() => setSidebarCollapsed(prev => !prev)}
                    onMobileMenuToggle={() => setMobileMenuOpen(prev => !prev)}
                />
                <div className="content-wrapper">
                    {children}
                </div>
            </div>
            {mobileMenuOpen && (
                <div
                    className="mobile-overlay"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-hidden="true"
                />
            )}
        </div>
    );
};

function AppContent() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={
                <ProtectedLayout>
                    <Dashboard />
                </ProtectedLayout>
            } />
            <Route path="/dashboard" element={
                <ProtectedLayout>
                    <Dashboard />
                </ProtectedLayout>
            } />
            <Route path="/menu" element={
                <ProtectedLayout>
                    <PermissionGate permission="staffCanManageMenu">
                        <MenuEditor />
                    </PermissionGate>
                </ProtectedLayout>
            } />
            <Route path="/menu-management" element={
                <ProtectedLayout>
                    <PermissionGate permission="staffCanManageMenu">
                        <MenuManagement />
                    </PermissionGate>
                </ProtectedLayout>
            } />
            <Route path="/branding" element={
                <ProtectedLayout>
                    <PermissionGate permission="staffCanViewBranding">
                        <BrandingPage />
                    </PermissionGate>
                </ProtectedLayout>
            } />
            <Route path="/upload" element={
                <ProtectedLayout>
                    <PermissionGate permission="staffCanViewDocuments">
                        <DocumentUpload />
                    </PermissionGate>
                </ProtectedLayout>
            } />
            <Route path="/documents" element={
                <ProtectedLayout>
                    <PermissionGate permission="staffCanViewDocuments">
                        <DocumentManagement />
                    </PermissionGate>
                </ProtectedLayout>
            } />
            <Route path="/events" element={
                <ProtectedLayout>
                    <PermissionGate permission="staffCanManageEvents">
                        <EventsManagement />
                    </PermissionGate>
                </ProtectedLayout>
            } />
            <Route path="/translation" element={
                <ProtectedLayout>
                    <PermissionGate permission="staffCanManageMenu">
                        <MenuTranslation />
                    </PermissionGate>
                </ProtectedLayout>
            } />
            <Route path="/payment-settings" element={
                <ProtectedLayout>
                    <PaymentSettingsPage />
                </ProtectedLayout>
            } />
            <Route path="/orders" element={
                <ProtectedLayout>
                    <FeatureGate feature="orderBoardEnabled">
                        <PermissionGate permission="staffCanViewOrders">
                            <OrdersManagementPage />
                        </PermissionGate>
                    </FeatureGate>
                </ProtectedLayout>
            } />
            <Route path="/delivery-touch" element={
                <ProtectedLayout>
                    <FeatureGate feature="orderBoardEnabled">
                        <PermissionGate permission="staffCanViewOrders">
                            <DeliveryTouchPage />
                        </PermissionGate>
                    </FeatureGate>
                </ProtectedLayout>
            } />
            <Route path="/cashflow" element={
                <ProtectedLayout>
                    <FeatureGate feature="cashflowEnabled">
                        <PermissionGate permission="staffCanViewCashflow">
                            <CashflowPage />
                        </PermissionGate>
                    </FeatureGate>
                </ProtectedLayout>
            } />
            <Route path="/print-center" element={
                <ProtectedLayout>
                    <FeatureGate feature="printCenterEnabled">
                        <PermissionGate permission="staffCanManagePrintCenter">
                            <PrintCenterPage />
                        </PermissionGate>
                    </FeatureGate>
                </ProtectedLayout>
            } />
            <Route path="/pos" element={
                <ProtectedLayout>
                    <FeatureGate feature="posEnabled">
                        <PosPage />
                    </FeatureGate>
                </ProtectedLayout>
            } />
            <Route path="/staff" element={
                <ProtectedLayout>
                    <PermissionGate permission="staffCanManageUsers">
                        <StaffManagement />
                    </PermissionGate>
                </ProtectedLayout>
            } />
            <Route path="/staff-permissions" element={
                <ProtectedLayout>
                    <PermissionGate permission="staffCanManageUsers">
                        <StaffPermissions />
                    </PermissionGate>
                </ProtectedLayout>
            } />
            <Route path="/client-features" element={
                <ProtectedLayout>
                    <PermissionGate permission="staffCanManageUsers">
                        <ClientFeatures />
                    </PermissionGate>
                </ProtectedLayout>
            } />
            <Route path="/support" element={
                <ProtectedLayout>
                    <SupportTickets />
                </ProtectedLayout>
            } />
            <Route path="/support/:id" element={
                <ProtectedLayout>
                    <TicketDetail />
                </ProtectedLayout>
            } />
        </Routes>
    );
}

function App() {
    return (
        <Router>
            <UserProvider>
                <AuthProvider>
                    <PermissionsProvider>
                        <FeatureProvider>
                            <TenantProvider>
                                <AppContent />
                            </TenantProvider>
                        </FeatureProvider>
                    </PermissionsProvider>
                </AuthProvider>
            </UserProvider>
        </Router>
    );
}

export default App;